import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { useGetRunDetail, useCancelRun, useGetClassificationTaxonomy } from "../api";
import type { MLExportRun, RunArtifact, RunMetrics } from "../types/run";
import { getRunErrorMessage } from "../types/run";
import type { TaxonomyFamily } from "../types/taxonomy";
import { Button } from "@/ui/components/ui/button";
import { Skeleton } from "@/ui/components/ui/skeleton";
import { Progress } from "@/ui/components/ui/progress";
import {
  XCircle,
  ArrowLeft,
  Clock,
  Timer,
  CheckCircle2,
  AlertTriangle,
  Ban,
  Loader2,
  Upload,
  ExternalLink,
  Copy,
  Cpu,
  RotateCcw,
} from "lucide-react";
import { cn } from "@/ui/lib/utils";
import { activeLocale, formatNumber } from "@/shared/lib/i18n/formatters.ts";
import toast from "react-hot-toast";
import { useMemo } from "react";
import PlanPreview from "../components/review/plan-preview";

/* ──────────────────────────────────────────────────────────────────
 * State-based run detail page.
 *
 * Layout:
 *   Header — always visible (destination, scope, timing)
 *   Central block — changes per state:
 *     PENDING   → queued pulse
 *     RUNNING   → progress bar + live counters
 *     SUCCESS   → PlanPreview (metrics reuse)
 *     FAILED    → error message
 *     CANCELLED → cancelled notice
 * ────────────────────────────────────────────────────────────────── */

/** Run status codes are wire values; only the badge text is translated. */
type RunDetailStatusKey =
  | "runDetail.status.pending"
  | "runDetail.status.running"
  | "runDetail.status.success"
  | "runDetail.status.partial"
  | "runDetail.status.failed"
  | "runDetail.status.cancelled";

const STATUS_CONFIG = {
  PENDING: {
    icon: Clock,
    labelKey: "runDetail.status.pending" as RunDetailStatusKey,
    color: "text-slate-500 dark:text-slate-300",
    bg: "bg-slate-500/10",
    border: "border-slate-500/20",
    dot: "bg-slate-400",
  },
  RUNNING: {
    icon: Loader2,
    labelKey: "runDetail.status.running" as RunDetailStatusKey,
    color: "text-blue-500",
    bg: "bg-blue-500/10",
    border: "border-blue-500/20",
    dot: "bg-blue-500",
  },
  SUCCESS: {
    icon: CheckCircle2,
    labelKey: "runDetail.status.success" as RunDetailStatusKey,
    color: "text-emerald-500",
    bg: "bg-emerald-500/10",
    border: "border-emerald-500/20",
    dot: "bg-emerald-500",
  },
  PARTIAL: {
    icon: AlertTriangle,
    labelKey: "runDetail.status.partial" as RunDetailStatusKey,
    color: "text-amber-500",
    bg: "bg-amber-500/10",
    border: "border-amber-500/20",
    dot: "bg-amber-500",
  },
  FAILED: {
    icon: AlertTriangle,
    labelKey: "runDetail.status.failed" as RunDetailStatusKey,
    color: "text-red-500",
    bg: "bg-red-500/10",
    border: "border-red-500/20",
    dot: "bg-red-500",
  },
  CANCELLED: {
    icon: Ban,
    labelKey: "runDetail.status.cancelled" as RunDetailStatusKey,
    color: "text-zinc-400",
    bg: "bg-zinc-500/10",
    border: "border-zinc-500/20",
    dot: "bg-zinc-400",
  },
} as const;

/* ─── Helpers ─────────────────────────────────────────────────── */

const formatDuration = (seconds: number, t: TFunction<"mlExports">): string => {
  if (seconds < 60)
    return t("runDetail.durationSeconds", {
      value: formatNumber(seconds, {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      }),
    });
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return t("runDetail.durationMinutes", {
    minutes: formatNumber(mins),
    seconds: formatNumber(secs),
  });
};

const formatDateTime = (iso: string): string =>
  new Date(iso).toLocaleString(activeLocale(), {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

const buildLabelColors = (
  families?: TaxonomyFamily[]
): Record<string, string> => {
  if (!families) return {};
  const map: Record<string, string> = {};
  for (const f of families) {
    for (const member of f.members) {
      map[member.canonical] = member.color;
    }
  }
  return map;
};

/* ─── Status badge ────────────────────────────────────────────── */

const StatusBadge = ({
  code,
  t,
}: {
  code: string;
  t: TFunction<"mlExports">;
}) => {
  const cfg = STATUS_CONFIG[code as keyof typeof STATUS_CONFIG] ?? STATUS_CONFIG.PENDING;
  const Icon = cfg.icon;
  return (
    <div className={cn(
      "inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-semibold",
      cfg.bg, cfg.border, cfg.color, "border",
    )}>
      <Icon className={cn("h-4 w-4", code === "RUNNING" && "animate-spin")} />
      {t(cfg.labelKey)}
    </div>
  );
};

/* ─── Header ──────────────────────────────────────────────────── */

const RunHeader = ({
  run,
  onCancel,
  cancelPending,
}: {
  run: MLExportRun;
  onCancel: () => void;
  cancelPending: boolean;
}) => {
  const { t } = useTranslation("mlExports");
  const navigate = useNavigate();
  const machines = run.summary.selection?.machines ?? [];
  const location = run.summary.selection?.location;

  const copyRunId = () => {
    navigator.clipboard.writeText(run.run_id);
    toast.success(t("runDetail.runIdCopied"));
  };

  return (
    <div className="space-y-4">
      {/* Back + actions */}
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          {t("runDetail.back")}
        </button>
        {!run.status.is_terminal && (
          <Button
            variant="outline"
            size="sm"
            onClick={onCancel}
            disabled={cancelPending}
            className="text-red-500 border-red-500/30 hover:bg-red-500/10"
          >
            <XCircle className="h-3.5 w-3.5 mr-1.5" />
            {t("runDetail.cancelRun")}
          </Button>
        )}
      </div>

      {/* Title row */}
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1 min-w-0">
          <div className="flex items-center gap-3">
            <StatusBadge code={run.status.code} t={t} />
            {run.status.stage && (
              <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded">
                {run.status.stage}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 pt-1">
            <span className="text-xs text-muted-foreground font-mono truncate">
              {run.run_id}
            </span>
            <button type="button" onClick={copyRunId} className="text-muted-foreground hover:text-foreground">
              <Copy className="h-3 w-3" />
            </button>
          </div>
        </div>
      </div>

      {/* Meta row */}
      <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 text-xs text-muted-foreground">
        {/* Destination */}
        <span className="inline-flex items-center gap-1.5">
          <ExternalLink className="h-3 w-3" />
          <span className="font-medium text-foreground">{run.destination.label}</span>
          {run.destination.project_id && (
            <span className="text-muted-foreground">/ {run.destination.project_id}</span>
          )}
        </span>

        {/* Location */}
        {location && (
          <span>
            {location.name}
          </span>
        )}

        {/* Machines */}
        {machines.length > 0 && (
          <span>
            {t("runDetail.machineCount", {
              count: machines.length,
              value: formatNumber(machines.length),
            })}
            {machines.length <= 3 &&
              t("runDetail.machineNames", {
                names: machines.map((m) => m.name).join(", "),
              })}
          </span>
        )}

        {/* Task kind */}
        <span className="font-mono text-[10px]">{run.task_kind}</span>
      </div>
    </div>
  );
};

/* ─── Artifact (dataset runs) ─────────────────────────────────────
 * A dataset run copies no images — its whole output is a manifest in the
 * mlops bucket. Without this section the run detail would show a successful
 * export with nothing to point a trainer at, so the gs:// URI is the payload
 * of the page, not a footnote.
 * ────────────────────────────────────────────────────────────────── */

const ArtifactSection = ({ artifact }: { artifact: RunArtifact }) => {
  const { t } = useTranslation("mlExports");
  const copyUri = () => {
    navigator.clipboard.writeText(artifact.uri);
    toast.success(t("runDetail.artifactUriCopied"));
  };

  return (
    <div className="space-y-2">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        {t("runDetail.artifactTitle")}
      </div>
      <div className="flex items-center gap-2 rounded-lg border bg-muted/40 px-3 py-2">
        <code className="flex-1 font-mono text-[11px] break-all text-foreground">
          {artifact.uri}
        </code>
        <button
          type="button"
          onClick={copyUri}
          className="shrink-0 text-muted-foreground hover:text-foreground"
          title={t("runDetail.copyArtifactUri")}
        >
          <Copy className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-muted-foreground">
        {artifact.record_count != null && (
          <span>
            <span className="font-medium text-foreground">
              {formatNumber(artifact.record_count)}
            </span>{" "}
            {t("runDetail.records")}
          </span>
        )}
        {artifact.manifest_hash && (
          <span className="inline-flex items-center gap-1.5">
            {t("runDetail.manifest")}
            <code className="font-mono text-[10px] text-foreground">
              {artifact.manifest_hash.slice(0, 12)}
            </code>
          </span>
        )}
      </div>
    </div>
  );
};

/* ─── Timing row ──────────────────────────────────────────────── */

const TimingRow = ({ run }: { run: MLExportRun }) => {
  const { t } = useTranslation("mlExports");
  const items = [
    { label: t("runDetail.timing.created"), value: run.timing.created_at },
    { label: t("runDetail.timing.started"), value: run.timing.started_at },
    { label: t("runDetail.timing.finished"), value: run.timing.finished_at },
  ].filter((i) => i.value != null);

  return (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-xs text-muted-foreground">
      {items.map((item) => (
        <span key={item.label} className="inline-flex items-center gap-1.5">
          <Timer className="h-3 w-3" />
          <span className="text-muted-foreground">{item.label}</span>
          <span className="text-foreground font-medium">{formatDateTime(item.value!)}</span>
        </span>
      ))}
      {run.timing.duration_seconds != null && (
        <span className="inline-flex items-center gap-1.5">
          <Clock className="h-3 w-3" />
          <span className="text-foreground font-medium tabular-nums">
            {formatDuration(run.timing.duration_seconds, t)}
          </span>
        </span>
      )}
    </div>
  );
};

/* ─── PENDING state ───────────────────────────────────────────── */

const PendingBlock = () => {
  const { t } = useTranslation("mlExports");
  return (
  <div className="flex flex-col items-center justify-center py-12 space-y-4">
    <div className="relative">
      <div className="h-16 w-16 rounded-full bg-slate-500/10 flex items-center justify-center">
        <Clock className="h-8 w-8 text-slate-500 dark:text-slate-300" />
      </div>
      <div className="absolute inset-0 h-16 w-16 rounded-full border-2 border-slate-500/30 animate-ping" />
    </div>
    <div className="text-center space-y-1">
      <p className="text-sm font-medium text-foreground">
        {t("runDetail.pending.title")}
      </p>
      <p className="text-xs text-muted-foreground">
        {t("runDetail.pending.description")}
      </p>
    </div>
  </div>
  );
};

/* ─── RUNNING state ───────────────────────────────────────────── */

const RunningBlock = ({ run }: { run: MLExportRun }) => {
  const { t } = useTranslation("mlExports");
  const pct = run.progress.percent ?? 0;
  const uploaded = run.progress.uploaded ?? 0;
  const total = run.progress.total_candidates ?? 0;

  return (
    <div className="space-y-4 py-4">
      {/* Progress header */}
      <div className="flex items-end justify-between">
        <div className="space-y-0.5">
          {run.progress.stage_title && (
            <p className="text-xs font-semibold text-foreground">
              {run.progress.stage_title}
            </p>
          )}
          {run.progress.stage_message && (
            <p className="text-xs text-muted-foreground">{run.progress.stage_message}</p>
          )}
        </div>
        <span className="text-2xl font-bold tabular-nums text-foreground">
          {t("runDetail.percent", { value: formatNumber(Math.round(pct)) })}
        </span>
      </div>

      {/* Progress bar */}
      <Progress value={pct} className="h-2.5" />

      {/* Counters */}
      <div className="flex items-center gap-6 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <Upload className="h-3 w-3" />
          <span className="font-medium text-foreground tabular-nums">
            {formatNumber(uploaded)}
          </span>
          {total > 0 && <span>/ {formatNumber(total)}</span>}
          <span>{t("runDetail.running.uploaded")}</span>
        </span>
        {run.summary.failed != null && run.summary.failed > 0 && (
          <span className="text-red-500">
            {t("runDetail.running.failedCount", {
              value: formatNumber(run.summary.failed),
            })}
          </span>
        )}
      </div>
    </div>
  );
};

/* ─── Result summary (for all terminal states) ───────────────── */

const ResultSummary = ({ run }: { run: MLExportRun }) => {
  const { t } = useTranslation("mlExports");
  const uploaded = run.summary.uploaded ?? run.progress.uploaded ?? 0;
  const total = run.progress.total_candidates ?? 0;
  const failed = run.summary.failed ?? 0;
  const machines = run.summary.selection?.machines ?? [];

  if (uploaded === 0 && total === 0) return null;

  return (
    <div className="rounded-lg border border-border bg-muted/20 p-4 space-y-3">
      {/* Upload stats */}
      <div className="grid grid-cols-3 gap-3">
        <div className="space-y-0.5">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
            {t("runDetail.summary.uploaded")}
          </div>
          <div className="text-lg font-bold tabular-nums">{formatNumber(uploaded)}</div>
        </div>
        <div className="space-y-0.5">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
            {t("runDetail.summary.total")}
          </div>
          <div className="text-lg font-bold tabular-nums text-muted-foreground">
            {formatNumber(total)}
          </div>
        </div>
        {failed > 0 && (
          <div className="space-y-0.5">
            <div className="text-[10px] uppercase tracking-wider text-red-500">
              {t("runDetail.summary.failed")}
            </div>
            <div className="text-lg font-bold tabular-nums text-red-500">
              {formatNumber(failed)}
            </div>
          </div>
        )}
      </div>

      {/* Progress bar showing how much was done */}
      {total > 0 && (
        <div className="space-y-1">
          <div className="flex h-2 rounded-full overflow-hidden bg-muted">
            {uploaded > 0 && (
              <div
                className="h-full bg-emerald-500 rounded-l-full"
                style={{ width: `${(uploaded / total) * 100}%` }}
              />
            )}
            {failed > 0 && (
              <div
                className="h-full bg-red-500"
                style={{ width: `${(failed / total) * 100}%` }}
              />
            )}
          </div>
          <div className="text-[10px] text-muted-foreground tabular-nums">
            {t("runDetail.summary.progress", {
              uploaded: formatNumber(uploaded),
              total: formatNumber(total),
              percent: formatNumber(Math.round((uploaded / total) * 100)),
            })}
          </div>
        </div>
      )}

      {/* Machines involved */}
      {machines.length > 0 && (
        <div className="flex flex-wrap gap-1.5 pt-1 border-t border-border/50">
          {machines.map((m) => (
            <span
              key={m.id}
              className="inline-flex items-center gap-1 text-[11px] bg-muted px-2 py-0.5 rounded"
            >
              <Cpu className="h-3 w-3 text-muted-foreground" />
              {m.name}
            </span>
          ))}
        </div>
      )}

      {/* Stage message */}
      {run.progress.stage_message && (
        <p className="text-xs text-muted-foreground italic">
          {run.progress.stage_message}
        </p>
      )}

      {/* Reopen the exact selection this run was built from, for re-review.
          The run records its image_ids server-side but does not yet return a
          selection spec — shown disabled rather than hidden so the capability
          is discoverable and the blocker is legible. Enable once MIN-251
          ships the spec. */}
      <div className="pt-1 border-t border-border/50">
        <Button
          size="sm"
          variant="outline"
          disabled
          title={t("runDetail.summary.reopenDisabled")}
        >
          <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
          {t("runDetail.summary.reopenSelection")}
        </Button>
      </div>
    </div>
  );
};

/* ─── SUCCESS state ───────────────────────────────────────────── */

const SuccessBlock = ({
  run,
  families,
  labelColors,
}: {
  run: MLExportRun;
  families?: TaxonomyFamily[];
  labelColors: Record<string, string>;
}) => {
  const metrics = run.metrics;

  return (
    <div className="space-y-4 py-2">
      {/* Always show result summary */}
      <ResultSummary run={run} />

      {/* Detailed metrics if available */}
      {metrics && (
        <PlanPreview
          plan={metrics as RunMetrics}
          families={families}
          labelColors={labelColors}
          destination={run.destination.code}
        />
      )}
    </div>
  );
};

/* ─── FAILED state ────────────────────────────────────────────── */

const FailedBlock = ({ run }: { run: MLExportRun }) => {
  const { t } = useTranslation("mlExports");
  const errorMessage = getRunErrorMessage(run.status.error);
  return (
    <div className="py-4 space-y-4">
      <div className="flex items-start gap-3 rounded-lg border border-red-500/30 bg-red-500/5 p-4">
        <AlertTriangle className="h-5 w-5 text-red-500 shrink-0 mt-0.5" />
        <div className="space-y-1">
          <p className="text-sm font-semibold text-red-600 dark:text-red-400">
            {t("runDetail.failed.title")}
          </p>
          {errorMessage ? (
            <p className="text-sm text-red-600/80 dark:text-red-400/80 font-mono whitespace-pre-wrap">
              {errorMessage}
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">
              {t("runDetail.failed.description")}
            </p>
          )}
        </div>
      </div>

      {/* Show what was done before failure */}
      <ResultSummary run={run} />
    </div>
  );
};

/* ─── PARTIAL state ───────────────────────────────────────────── */

const PartialBlock = ({ run }: { run: MLExportRun }) => {
  const { t } = useTranslation("mlExports");
  const uploaded = run.summary.uploaded ?? run.progress.uploaded ?? 0;
  const total = run.progress.total_candidates ?? 0;
  const errorMessage = getRunErrorMessage(run.status.error);

  return (
    <div className="py-4 space-y-4">
      <div className="flex items-start gap-3 rounded-lg border border-amber-500/30 bg-amber-500/5 p-4">
        <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
        <div className="space-y-1">
          <p className="text-sm font-semibold text-amber-600 dark:text-amber-400">
            {t("runDetail.partial.title")}
          </p>
          <p className="text-sm text-amber-700/90 dark:text-amber-300/90">
            {t("runDetail.partial.description")}
          </p>
          {/* Always show uploaded count for PARTIAL — the destination dataset
              may already contain some of these images. Shown even if total is 0
              so the result-summary null-guard can't hide it. */}
          <p className="text-sm font-medium text-amber-700 dark:text-amber-300 tabular-nums pt-1">
            {total > 0
              ? t("runDetail.partial.uploadedOfTotal", {
                  uploaded: formatNumber(uploaded),
                  total: formatNumber(total),
                })
              : t("runDetail.partial.uploaded", {
                  uploaded: formatNumber(uploaded),
                })}
          </p>
          {errorMessage && (
            <p className="text-xs text-amber-700/80 dark:text-amber-300/80 font-mono whitespace-pre-wrap pt-1">
              {errorMessage}
            </p>
          )}
        </div>
      </div>

      {/* Richer breakdown when counts are present */}
      <ResultSummary run={run} />
    </div>
  );
};

/* ─── CANCELLED state ─────────────────────────────────────────── */

const CancelledBlock = ({ run }: { run: MLExportRun }) => {
  const { t } = useTranslation("mlExports");
  return (
  <div className="py-4 space-y-4">
    <div className="flex items-start gap-3 rounded-lg border border-zinc-500/20 bg-zinc-500/5 p-4">
      <Ban className="h-5 w-5 text-zinc-400 shrink-0 mt-0.5" />
      <div className="space-y-1">
        <p className="text-sm font-medium text-muted-foreground">
          {t("runDetail.cancelled.title")}
        </p>
      </div>
    </div>

    {/* Show what was done before cancellation */}
    <ResultSummary run={run} />
  </div>
  );
};

/* ─── Loading skeleton ────────────────────────────────────────── */

const LoadingSkeleton = () => (
  <div className="p-6 max-w-3xl space-y-6">
    <div className="space-y-3">
      <Skeleton className="h-5 w-16" />
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-4 w-72" />
    </div>
    <Skeleton className="h-px w-full" />
    <div className="space-y-4">
      <Skeleton className="h-4 w-64" />
      <Skeleton className="h-40 w-full rounded-lg" />
      <Skeleton className="h-24 w-full rounded-lg" />
    </div>
  </div>
);

/* ─── Main page ───────────────────────────────────────────────── */

const ExportRunDetailPage = () => {
  const { t } = useTranslation("mlExports");
  const { runId } = useParams<{ runId: string }>();
  const { data: run, isLoading } = useGetRunDetail(runId ?? "", !!runId);
  const cancelMutation = useCancelRun();
  const { data: taxonomy } = useGetClassificationTaxonomy();

  const labelColors = useMemo(
    () => buildLabelColors(taxonomy?.families),
    [taxonomy?.families]
  );

  const handleCancel = async () => {
    if (!runId) return;
    try {
      await cancelMutation.mutateAsync(runId);
      toast.success(t("runDetail.cancelRequested"));
    } catch {
      toast.error(t("runDetail.cancelFailed"));
    }
  };

  if (isLoading) return <LoadingSkeleton />;

  if (!run) {
    return (
      <div className="p-6 max-w-3xl">
        <p className="text-muted-foreground">{t("runDetail.notFound")}</p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-3xl space-y-5">
      {/* ─── Header ──────────────────────────────── */}
      <RunHeader
        run={run}
        onCancel={handleCancel}
        cancelPending={cancelMutation.isPending}
      />

      <div className="h-px bg-border" />

      {/* ─── Timing ──────────────────────────────── */}
      <TimingRow run={run} />

      <div className="h-px bg-border" />

      {/* ─── State-dependent block ────────────────── */}
      {run.status.code === "PENDING" && <PendingBlock />}
      {run.status.code === "RUNNING" && <RunningBlock run={run} />}
      {run.status.code === "SUCCESS" && (
        <SuccessBlock
          run={run}
          families={taxonomy?.families}
          labelColors={labelColors}
        />
      )}
      {run.status.code === "PARTIAL" && <PartialBlock run={run} />}
      {run.status.code === "FAILED" && <FailedBlock run={run} />}
      {run.status.code === "CANCELLED" && <CancelledBlock run={run} />}

      {/* ─── Artifact — dataset runs only ─────────── */}
      {run.artifact && (
        <>
          <div className="h-px bg-border" />
          <ArtifactSection artifact={run.artifact} />
        </>
      )}
    </div>
  );
};

export default ExportRunDetailPage;
